import { logError } from '../../lib/logger';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { adminGetMembers, adminSetPasscode, adminRevokeAccess, adminAddMember } from '../../lib/api';
import { Lock, UserMinus, UserPlus, Check, Eye, EyeSlash, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLES = ['SA', 'MC', 'AVP', 'LC', 'PM', 'Admin'];

export default function TeamAccess() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passcodeDialogOpen, setPasscodeDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newPasscode, setNewPasscode] = useState('');
  const [addForm, setAddForm] = useState({ name: '', initials: '', role: 'SA', passcode: '' });
  const [visiblePasscodes, setVisiblePasscodes] = useState({});
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try { setMembers(await adminGetMembers()); }
    catch (e) { logError('Admin load error:', e); toast.error('Failed to load team'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSetPasscode = async () => {
    if (newPasscode.length !== 4 || !/^\d{4}$/.test(newPasscode)) { toast.error('Must be 4 digits'); return; }
    try {
      await adminSetPasscode(selectedMember.id, newPasscode);
      toast.success(`Passcode updated for ${selectedMember.name}`);
      setPasscodeDialogOpen(false); setNewPasscode(''); loadData();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleToggleAccess = async (member) => {
    try {
      await adminRevokeAccess(member.id, !member.revoked);
      toast.success(member.revoked ? `${member.name} restored` : `${member.name} revoked`);
      loadData();
    } catch (e) { toast.error('Failed'); }
  };

  const handleAddMember = async () => {
    if (!addForm.name || !addForm.initials) { toast.error('Name and initials required'); return; }
    try {
      await adminAddMember({ ...addForm, group: addForm.role });
      toast.success(`${addForm.name} added`);
      setAddDialogOpen(false); setAddForm({ name: '', initials: '', role: 'SA', passcode: '' }); loadData();
    } catch (e) { toast.error('Failed to add'); }
  };

  const filtered = members.filter(m =>
    !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.initials.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-4" data-testid="team-access-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-foreground">Team Access</h2>
          <p className="text-xs text-muted-foreground mt-1">Manage members, passcodes, and access. Existing security controls unchanged.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 h-9 w-56 border-border" data-testid="team-access-search" />
          </div>
          <Button onClick={() => setAddDialogOpen(true)} className="bg-[#E63935] hover:bg-[#d32f2f] text-white h-9" data-testid="add-member-btn">
            <UserPlus size={16} className="mr-1.5" /> Add Team Member
          </Button>
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left p-3 font-medium">Member</th>
                  <th className="text-left p-3 font-medium">Role</th>
                  <th className="text-left p-3 font-medium">Passcode</th>
                  <th className="text-left p-3 font-medium">Last Sign In</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-right p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors" data-testid={`admin-member-${m.initials.toLowerCase()}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2.5">
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold ${m.revoked ? 'bg-red-500/20 text-red-500' : 'bg-[#E63935]/20 text-[#E63935]'}`}>{m.initials}</span>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{m.name}</p>
                          {m.is_admin && <p className="text-[10px] text-[#E63935] font-semibold">ADMIN</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{m.role || '—'}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2" data-testid={`passcode-${m.initials.toLowerCase()}`}>
                        <span className="font-mono text-sm text-foreground tracking-widest">{visiblePasscodes[m.id] ? (m.passcode_plain || '----') : '****'}</span>
                        <button onClick={() => setVisiblePasscodes(p => ({ ...p, [m.id]: !p[m.id] }))} className="text-muted-foreground hover:text-foreground" data-testid={`toggle-show-passcode-${m.initials.toLowerCase()}`}>
                          {visiblePasscodes[m.id] ? <EyeSlash size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground" data-testid={`last-login-${m.initials.toLowerCase()}`}>
                      {m.last_login ? new Date(m.last_login).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                    </td>
                    <td className="p-3">
                      {m.revoked
                        ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 font-semibold">REVOKED</span>
                        : <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-500 font-semibold">ACTIVE</span>}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button variant="outline" size="sm" onClick={() => { setSelectedMember(m); setNewPasscode(''); setPasscodeDialogOpen(true); }}
                          className="border-border h-8 text-xs" data-testid={`set-passcode-${m.initials.toLowerCase()}`}>
                          <Lock size={11} className="mr-1" /> Passcode
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleToggleAccess(m)}
                          className={`h-8 text-xs ${m.revoked ? 'border-emerald-500/30 text-emerald-500' : 'border-red-500/30 text-red-500'}`}
                          data-testid={`toggle-access-${m.initials.toLowerCase()}`}>
                          {m.revoked ? <><Check size={11} className="mr-1" /> Restore</> : <><UserMinus size={11} className="mr-1" /> Revoke</>}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={passcodeDialogOpen} onOpenChange={setPasscodeDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="passcode-dialog">
          <DialogHeader><DialogTitle>Set Passcode for {selectedMember?.name}</DialogTitle></DialogHeader>
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 block">New 4-digit passcode</label>
            <Input type="password" value={newPasscode} onChange={e => setNewPasscode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000" className="border-border h-12 text-2xl text-center tracking-[0.5em] font-mono" maxLength={4} data-testid="new-passcode-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasscodeDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSetPasscode} className="bg-[#E63935] text-white" data-testid="save-passcode-btn">Save Passcode</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="add-member-dialog">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Full Name</label>
                <Input value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} className="border-border h-11" data-testid="add-member-name" /></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Initials</label>
                <Input value={addForm.initials} onChange={e => setAddForm(p => ({ ...p, initials: e.target.value.toUpperCase().slice(0, 3) }))} className="border-border h-11" maxLength={3} data-testid="add-member-initials" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Role</label>
                <Select value={addForm.role} onValueChange={v => setAddForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="border-border h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-sm font-medium text-muted-foreground mb-1 block">Passcode</label>
                <Input type="password" value={addForm.passcode} onChange={e => setAddForm(p => ({ ...p, passcode: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                  placeholder="4 digits" className="border-border h-11" maxLength={4} data-testid="add-member-passcode" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleAddMember} className="bg-[#E63935] text-white" data-testid="confirm-add-member">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
